import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { FormContainer, UseFormReturn } from 'react-hook-form-mui';
import { APIContext, DialogAction } from '../../Contexts/APIContext';
import { SessionContext } from '../../Contexts/SessionContext';
import yesNoDropdown from '../../Utilities/Constants/YesNoDropdown';
import DataContainer from '../../Utilities/DataContainer';
import EditableTableCell from '../../Utilities/Forms/EditableTableCell';
import RenderSwitchField from '../../Utilities/Forms/RenderCheckBoxField';
import ValidatedSelectElement from '../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../Utilities/Forms/ValidatedTextField';
import { PhotoPreview } from '../../Utilities/PhotoPreview';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';
import SiteViewModel from '../ViewModels/SiteViewModel';
import Stack from '@mui/material/Stack';
import { useLocation, useNavigate } from 'react-router-dom';
import PDFLink from '../../Popups/PDFLink';
import WarningPopup from '../../Popups/WarningPopup';
import { getSiblingSiteUrl } from '../SiteNavigation';
import EditControl from './EditControl';
import DescriptionsTable from './DescriptionsTable';
import RoleCheck from '../../Authorisation/RoleCheck';
import TextField from '@mui/material/TextField';
import NumberInputField from '../../Utilities/Forms/NumberInputField';
import DftConnectivityToolScores from '../DftConnectivityToolScores';

const RenderTextField = ({ label, name, readOnly, endAdornment, forceValue, readOnlyFormat, type }: { label: string; name: string; readOnly: boolean; endAdornment?: string, forceValue?: any, readOnlyFormat?: any, type?: string }) => (
    <Grid container size={12} alignItems="center">
        <Grid >
            <Typography>{label}</Typography>
        </Grid>
        <Grid >
            {(forceValue != undefined) ?
                <TextField
                    fullWidth
                    size="small"
                    value={forceValue}
                />
                :
                name.length !== 0 && (
                    <ValidatedTextField name={name} readOnly={readOnly} endAdornment={endAdornment} readOnlyFormat={readOnlyFormat} type={type} />
                )
            }
        </Grid>
    </Grid>
);

const RenderSelect = ({ label, name, options, readOnly, help }: { label: string; name: string; options: DropDownViewModel[]; readOnly: boolean, help?: JSX.Element }) => (
    <Grid container size={12} alignItems="center">
        <Grid>
            <Typography>{label}</Typography>
        </Grid>
        {help &&
            <>
                <Grid size={{ xs: 'grow' }} />
                <Grid>
                    {help}
                </Grid>
            </>
        }
        <Grid>
            <ValidatedSelectElement name={name} options={options} readOnly={readOnly} />
        </Grid>
    </Grid>
);

const CustomTableRow = ({ label, name, value, readOnly, endAdornment }: { label: string; name: string; value: string | number | null; readOnly: boolean; endAdornment?: string }) => (
    <TableRow key={label}>
        <TableCell align="left">{label}</TableCell>
        <EditableTableCell name={name} value={value} readOnly={readOnly} endAdornment={endAdornment} />
    </TableRow>
)

const CustomTableHead = ({ title }) => (
    <TableHead>
        <TableRow>
            <TableCell>{title}</TableCell>
            <TableCell></TableCell>
        </TableRow>
    </TableHead>
)

const hillyOrFlatDropdown = [
    { id: "2", label: 'Hilly' },
    { id: "3", label: 'Flat' }
];

const travelPlanDropdown = [
    { id: 'N', label: 'No' },
    { id: 'Y', label: 'Yes' },
    { id: 'U', label: 'Not Known' }
];


const speedLimitDropdown = [
    { id: "1", label: 'N/A' },
    { id: "2", label: '20' },
    { id: "3", label: '30' },
    { id: "4", label: '40' },
    { id: "5", label: '50' },
    { id: "6", label: '60' },
    { id: "7", label: '70' }
];
const parseTricsVersionParts = (value?: string | null): number[] | null => {
    if (!value) {
        return null;
    }

    // Supports values like "7.10.2" and legacy formats like "2012(b)v6.10.2".
    const match = value.match(/(\d+)\.(\d+)\.(\d+)$/);
    if (!match) {
        return null;
    }

    return match.slice(1).map((part) => Number(part));
};

const isTricsVersionGreaterThan = (value: string | null | undefined, minimumExclusive: string): boolean => {
    const current = parseTricsVersionParts(value);
    const minimum = parseTricsVersionParts(minimumExclusive);
    if (!current || !minimum) {
        return false;
    }

    for (let i = 0; i < 3; i++) {
        if (current[i] > minimum[i]) {
            return true;
        }
        if (current[i] < minimum[i]) {
            return false;
        }
    }

    return false;
};
const WIDTH_RESTRICTIONS_RELEASE_DATE = new Date(2026, 2, 7);

const parseSiteUploadDate = (value?: string | null): Date | null => {
    if (!value) {
        return null;
    }

    const trimmedValue = value.trim();
    const ukDateMatch = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ukDateMatch) {
        const day = Number(ukDateMatch[1]);
        const month = Number(ukDateMatch[2]);
        const year = Number(ukDateMatch[3]);
        const parsedUkDate = new Date(year, month - 1, day);

        if (parsedUkDate.getFullYear() === year && parsedUkDate.getMonth() === month - 1 && parsedUkDate.getDate() === day) {
            return parsedUkDate;
        }

        return null;
    }

    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
};

const isAfterWidthRestrictionsReleaseDate = (uploadDate?: string | null): boolean => {
    const parsedUploadDate = parseSiteUploadDate(uploadDate);
    if (!parsedUploadDate) {
        return false;
    }

    return parsedUploadDate.getTime() > WIDTH_RESTRICTIONS_RELEASE_DATE.getTime();
};

const NUMBER_OF_EMPLOYEES_LAND_USES = new Set([
    '01/R', '01/S',
    '05/A', '05/B', '05/D', '05/F', '05/H', '05/K', '05/M', '05/N',
    '06/B', '06/C', '06/D', '06/F', '06/G', '06/H', '06/I', '06/J', '06/K',
    '02/F', '02/I'
]);

const EMPLOYEE_SPLIT_LAND_USES = new Set(['02/A', '02/E', '02/G']);

const isEmployeeStatistic = (label?: string | null): boolean =>
    label?.toLowerCase().includes('employee') ?? false;

interface IProps {
    reloadSite: () => void;
    siteFormContext: UseFormReturn<SiteViewModel>;
}

export default function LocationDetails({ reloadSite, siteFormContext }: IProps) {
    const { get, post, showMessage, openDialog } = useContext(APIContext);
    const [locationDropdown, setLocationDropdown] = useState<DropDownViewModel[]>([]);
    const [subLocationDropdown, setSubLocationDropDown] = useState<DropDownViewModel[]>([]);
    const [useClassDropdown, setUseClassDropdown] = useState<DropDownViewModel[]>([]);
    const [carOwnershipDropdown, setCarOwnershipDropdown] = useState<DropDownViewModel[]>([]);
    const [popFiveMileDropdown, setPopFiveMileDropdown] = useState<DropDownViewModel[]>([]);
    const [popOneMileDropdown, setPopOneMileDropdown] = useState<DropDownViewModel[]>([]);
    const [ptal, setptal] = useState<DropDownViewModel[]>([]);
    const [smallPhotoUrl, setSmallPhotoUrl] = useState<string>('');
    const { sessionState } = useContext(SessionContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [readOnly, setReadOnly] = useState<boolean>(true);

    const { watch } = siteFormContext;

    const site = watch();
    const landUseCode = `${sessionState.siteListMain}/${sessionState.siteListSub}`;
    const showNumberOfEmployees = NUMBER_OF_EMPLOYEES_LAND_USES.has(landUseCode);
    const showEmployeeSplit = EMPLOYEE_SPLIT_LAND_USES.has(landUseCode);
    const suppressGenericEmployeeStatistic = (label?: string | null): boolean =>
        (showNumberOfEmployees || showEmployeeSplit) && isEmployeeStatistic(label);

    const hasParkingDetails = watch("hasParkingDetails", false) ?? false;
    const showLocalTrafficCalmingFeatures = isTricsVersionGreaterThan(site.version, "7.10.1");
    const showWidthRestrictions = isAfterWidthRestrictionsReleaseDate(site.dateCreated);

    useEffect(() => {
        getDropdowns();
    }, []);

    useEffect(() => {
        reloadSite();
    }, [readOnly]);

    useEffect(() => {
        get('Site', `GetPhoto?reference=${site.reference}`, setSmallPhotoUrl)
    }, [site.reference]);

    const getDropdowns = () => {
        get('Site', `GetLocationDropDown`, setLocationDropdown)
        get('Site', `GetUseClassDropDown`, setUseClassDropdown)
        get('Site', `GetSubLocationDropDown`, setSubLocationDropDown)
        get('Site', `GetPopulationOneMileDropDown`, setPopOneMileDropdown)
        get('Site', `GetPopulationFiveMilesDropDown`, setPopFiveMileDropdown)
        get('Site', `GetCarOwnershipDropDown`, setCarOwnershipDropdown)
        get('Site', `GetPtalDropdown`, setptal)
    }

    const submit = async (form: any) => {
        await post('Site', `UpdateLocationDetails?reference=${site.reference}`, form, () => {
            showMessage('success', 'Location details updated successfully')
            setReadOnly(true);
        });
    }

    const navigateToUrl = () => {
        const url = `https://datashine.org.uk/#table=QS411EW&col=QS411EW0007&ramp=YlOrRd&layers=BTTT&zoom=14&lon=${site.longitude}&lat=${site.latitude}`;
        window.open(url, '_blank');
    };

    const openInGoogleMaps = () => {
        openDialog(
            "You are about to open a separate browser session which accesses Google Maps, referenced by this site's Latitude and Longitude data fields. To reproduce images from Google Maps in your reports or any other submitted outputs, permission must be obtained from Google. Links to Google's Permission Rules and a Permission Request Form are immediately below.Adherence to the stated rules is the sole responsibility of individual TRICS users.",
            [
                new DialogAction("Permission Rules", () => {
                    const url = `https://about.google/brand-resource-center/`;
                    window.open(url, "_blank");
                }),
                new DialogAction("Permission Form", () => {
                    const url = `https://support.google.com/contact/brand_request_form`;
                    window.open(url, "_blank");
                }),
                new DialogAction("Continue to Map", () => {
                    const url = `https://www.google.com/maps?q=${site.latitude},${site.longitude}`;
                    window.open(url, "_blank");
                })
            ],
            "Google Maps",
            "Cancel",
            "lg"
        );
    };

    const testSiteStatistics = (label: string) => {
        return label != '' && label != null && !label.includes("*");
    }

    const isScotland = site.region === 'SCOTLAND';

    const isIreland = site.region === 'ULSTER (REPUBLIC OF IRELAND)'
        || site.region === 'GREATER DUBLIN'
        || site.region === 'LEINSTER'
        || site.region === 'MUNSTER'
        || site.region === 'CONNAUGHT';

    const isNorthanIreland = site.region === "ULSTER (NORTHERN IRELAND)";

    const hasEnglandAndWalesCensusMap =
        (!isScotland
            && !isIreland
            && !isNorthanIreland)
        && site.longitude
        && site.latitude;

    const isLondon = site.region === 'GREATER LONDON';
    const showRoadNetworkDistances = site.mostRecentSurveyDateInt <= 20270101;

    const format = (value: string | number | null | undefined): string => {
        if (value == null || value === '') {
            return '';
        }

        const numericValue = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(numericValue)) {
            return Number.isInteger(numericValue)
                ? numericValue.toString()
                : numericValue.toFixed(2);
        }

        return String(value);
    }

    const censusLabel = isIreland ? "County" : "Census Output Area/Data Zone";

    return (
        <FormContainer formContext={siteFormContext} onSuccess={submit}>
            <Grid container alignItems="flex-start">
                <Grid container size={4} alignItems="flex-start">
                    <DataContainer title="Location Details">
                        <RenderTextField label="Description" name="description" readOnly={readOnly} />
                        <RenderTextField label="Street" name="street" readOnly={readOnly} />
                        <RenderTextField label="District" name="district" readOnly={readOnly} />
                        <RenderTextField label="Town" name="city" readOnly={readOnly} />
                        <RenderTextField label="Postcode" name="postcode" readOnly={readOnly} />
                        <RenderTextField label="Planning Authority" name="planningAuthority" readOnly={readOnly} />
                        <NumberInputField
                            label="Latitude"
                            name="latitude"
                            readOnly={readOnly}
                            decimalLength={5}
                        />
                        <NumberInputField
                            label="Longitude"
                            name="longitude"
                            readOnly={readOnly}
                            decimalLength={5}
                        />
                        {site.longitude != null && site.latitude != null &&
                            <Button onClick={openInGoogleMaps}>Open Location Map</Button>
                        }
                        <RenderSelect label="Location" name="location" options={locationDropdown} readOnly={readOnly} />
                        <RenderSelect label="Locn Sub Cat" name="locnSubCategory" options={subLocationDropdown} readOnly={readOnly} help={<PDFLink container="GeneralDocs" file="Location Definition" />} />
                        <RenderSelect label="Use class" name="useClass" options={useClassDropdown} readOnly={readOnly} help={<PDFLink container="GeneralDocs" file="Use Classes Order" />} />
                        <Grid size={12}>
                            <PDFLink label="Land Uses & Use Classes" container="GeneralDocs" file="TRICS Use Classes Land Uses" />
                        </Grid>
                        <PhotoPreview src={smallPhotoUrl} />
                    </DataContainer>

                    {(site.landUse === '02') && (
                        <DataContainer title="Breakdown of Site Operations" >
                            <RenderTextField label="Offices" name="siteOffices" readOnly={readOnly} endAdornment="%" />
                            <RenderTextField label="Research & Development" name="siteResearchAndDev" readOnly={readOnly} endAdornment="%" />
                            <RenderTextField label="Light Industrial" name="siteLightIndustrial" readOnly={readOnly} endAdornment="%" />
                            <RenderTextField label="General Industrial" name="siteGeneralIndustrial" readOnly={readOnly} endAdornment="%" />
                            <RenderTextField label="Storage & Distribution" name="siteStorageAndDistribution" readOnly={readOnly} endAdornment="%" />
                        </DataContainer>
                    )}

                    {showLocalTrafficCalmingFeatures && (
                        <DataContainer title="Local Traffic Calming Features" >
                            <RenderSelect label="Speed Limit (mph)" name="speedLimit" readOnly={readOnly} options={speedLimitDropdown} help={
                                <WarningPopup type="warning" title="Note on Local Traffic Calming Features" size='medium' content={
                                    <>
                                        <Typography>The speed limit shown represents the speed limit (mph) on the road off which the site's main access is located. This main access can either be a vehicular access or a pedestrian access (whichever is most relevant). Any ticked traffic-calming features will be located within 200 metres road distance of the site. They will be unticked if such features only exist outside of 200 metres road distance. The definitions of these features are as follows.</Typography>
                                        <Typography><b><u>Speed bumps</u></b></Typography>
                                        <Typography>Any physical elevations in the road designed to lower the speed of vehicles passing over them.</Typography>
                                        <Typography><b><u>Speed cameras</u></b></Typography>
                                        <Typography>Any temporary (at the time of the survey) or permanent fixed speed cameras.</Typography>
                                        <Typography><b><u>Give way restrictions</u></b></Typography>
                                        <Typography>Any fixed structures which are signed for giving way to oncoming vehicles, plus any single lane bridges or similar which are also signed in a similar way to manage bi-directional flow.</Typography>
                                        <Typography><b><u>Stopping restrictions</u></b></Typography>
                                        <Typography>Any restrictions on vehicles stopping/parking on-street. Examples include single and double yellow lines, red routes, and any specific areas where "no stopping/parking" indications are present for any specified times of the day.</Typography>
                                        <Typography><b><u>Width restrictions</u></b></Typography>
                                        <Typography>Any physical fixed restriction on vehicle width, which allows no vehicle with a greater width than the restricted limit to pass.</Typography>
                                        <Typography><b><u>Bus lanes</u></b></Typography>
                                        <Typography>Any bus lanes that operate with restrictions for other vehicle types for any part of the day, including bus lanes allowing use by motorcycles, pedal cycles and taxis.</Typography>
                                        <Typography><b><u>20 mph roads</u></b></Typography>
                                        <Typography>Any roads where there are set 20 miles per hour speed limits, including roads with 20mph limits at certain times of the day (for example where schools are located and this is indicated accordingly).  Excludes any roads with "advisory" 20mph limits which are not legal limits.</Typography>

                                    </>
                                }
                                />} />
                            <RenderSwitchField name={'speedBumps'} label={"Speed bumps"} readOnly={readOnly} switchSize={7} />
                            <RenderSwitchField name={'speedCameras'} label={"Speed cameras"} readOnly={readOnly} switchSize={7} />
                            <RenderSwitchField name={'giveWayRestirctions'} label={"Give way restrictions"} readOnly={readOnly} switchSize={7} />
                            <RenderSwitchField name={'stoppingRestrictions'} label={"Stopping restrictions"} readOnly={readOnly} switchSize={7} />
                            {showWidthRestrictions && <RenderSwitchField name={'widthRestrictions'} label={"Width restrictions"} readOnly={readOnly} switchSize={7} />}
                            <RenderSwitchField name={'busLanes'} label={"Bus lanes"} readOnly={readOnly} switchSize={7} />
                            <RenderSwitchField name={'mphRoads'} label={"20 mph roads"} readOnly={readOnly} switchSize={7} />
                        </DataContainer>
                    )}
                </Grid>
                <Grid container size={8} alignItems={"flex-start"} >
                    <Grid size={12}>
                        <DataContainer title="Comments" >
                            <ValidatedTextField multiline={true} rows={6} name={'comments'} readOnly={readOnly} />
                        </DataContainer>
                    </Grid>
                    <Grid size={6} container>
                        <DataContainer title="Site Statistics" >
                            {testSiteStatistics(site.param1Label) && !suppressGenericEmployeeStatistic(site.param1Label) && <RenderTextField label={site.param1Label} name="param1Value" readOnly={readOnly} endAdornment={site.param1Unit} readOnlyFormat={format} />}
                            {showEmployeeSplit ?
                                <>
                                    {site.partTimeEmployees == null ?
                                        <RenderTextField label="Number of Employees" name="" readOnly forceValue={site.numberOfEmployees ?? ""} />
                                        :
                                        <>
                                            <RenderTextField label="Total full time employees" name="" readOnly forceValue={site.fullTimeEmployees ?? ""} />
                                            <RenderTextField label="Total part time employees" name="" readOnly forceValue={site.partTimeEmployees} />
                                        </>
                                    }
                                </>
                                :
                                !(sessionState.siteListMain === '02' && sessionState.siteListSub === 'B') &&
                                testSiteStatistics(site.param2Label) && !suppressGenericEmployeeStatistic(site.param2Label) && <RenderTextField label={site.param2Label} name="param2Value" readOnly={readOnly} endAdornment={site.param2Unit} readOnlyFormat={format} />
                            }
                            {testSiteStatistics(site.param3Label) && !suppressGenericEmployeeStatistic(site.param3Label) && <RenderTextField label={site.param3Label} name="param3Value" readOnly={readOnly || !hasParkingDetails} readOnlyFormat={format} />}
                            {testSiteStatistics(site.param4Label) && !suppressGenericEmployeeStatistic(site.param4Label) && <RenderTextField label={site.param4Label} name="param4Value" readOnly={readOnly} readOnlyFormat={format} />}
                            {showNumberOfEmployees && <RenderTextField label="Number of Employees" name="" readOnly forceValue={site.numberOfEmployees ?? ""} />}
                            <RenderTextField label="No. of development for this site" name="numberOfDevelopments" readOnly={readOnly} />
                            <RenderTextField label="No. of survey days for this site" name="numberOfSurveyDays" readOnly={readOnly} />
                            {hasEnglandAndWalesCensusMap &&
                                <Grid size={12}>
                                    <Typography>Car ownership per household 2011 (England & Wales)</Typography>
                                    <Button onClick={navigateToUrl} sx={{ mt: 1 }}>View Census Map</Button>
                                </Grid>
                            }
                        </DataContainer>
                        <DataContainer title="Population Data" >
                            <RenderSelect label="Population within 1 mile" name="oneMilePopulation" options={popOneMileDropdown} readOnly={readOnly} />
                            <RenderSelect label="Population within 5 miles" name="fiveMilePopulation" options={popFiveMileDropdown} readOnly={readOnly} />
                            <RenderTextField label="Population within 500m" name="population500m" readOnly={readOnly} />
                            <RenderSelect label="Car ownership (5 miles)" name="carOwnershipFiveMiles" options={carOwnershipDropdown} readOnly={readOnly} />
                            <RenderSelect label="Is the location of the site hilly or flat" name="hillyOrFlat" readOnly={readOnly} options={hillyOrFlatDropdown} />
                        </DataContainer>
                    </Grid>
                    <Grid container size={6} alignItems="flex-start">
                        <DataContainer title="Created" >
                            <RenderTextField label="Version" name="version" readOnly={readOnly} />
                            <RenderTextField label="Date" name="dateCreated" readOnly={readOnly} />
                        </DataContainer>
                        <DataContainer title="Travel Plan" >
                            <RenderSelect label="Is there a Travel Plan associated with this site?" name="travelPlan" options={travelPlanDropdown} readOnly={readOnly} />
                            <RenderSelect label="If not, are there any plans to implement a Travel Plan in the future?" name="isTravelPlanInFuture" options={yesNoDropdown} readOnly={readOnly} />
                            <RenderSelect label="Is survey data available before the implementation of the Travel Plan?" name="isDataAvailablePriorToTravelPlan" options={yesNoDropdown} readOnly={readOnly} />
                            <RenderSelect label="Urban Regeneration" name="isUrbanRegeneration" options={yesNoDropdown} readOnly={readOnly} />
                            <RenderSelect label="Standard Assessment Methodology (SAM)" name="isSAM" options={yesNoDropdown} readOnly={readOnly} />
                            {sessionState.siteListMain === "04" && sessionState.siteListSub === "A" &&
                                <RenderSelect label="Is a nursery included at this site?" name="nurseryOnSite" options={yesNoDropdown} readOnly={readOnly} />
                            }
                            {(site.sixthFormOnSite !== undefined && site.sixthFormOnSite !== null) &&
                                <RenderSelect label="Is a sixth form included at this site?" name="sixthFormOnSite" options={yesNoDropdown} readOnly={readOnly} />
                            }
                            <RenderSelect label="Covid-19 Restrictions" name="isCovid19" options={yesNoDropdown} readOnly={readOnly} />
                            {isLondon &&
                                <>
                                    <RenderSelect label="PTAL Rating" name="ptalRating" options={ptal} readOnly={readOnly} />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() =>
                                            window.open(
                                                "https://experience.arcgis.com/experience/4c88d5310df34e21bcb3a50ae9c0a159",
                                                "_blank"
                                            )
                                        }
                                    >
                                        More on PTAL Calculation
                                    </Button>
                                </>
                            }
                        </DataContainer>
                        {((site.previousReSurveyReference !== null || site.nextReSurveyReference !== null) || !readOnly) &&
                            <DataContainer title="Re-Survey History">
                                {(site.previousReSurveyReference !== null || !readOnly) &&
                                    <Grid size={12}>
                                        <Stack direction={"row"} spacing={1}>
                                            <Grid size={9}>
                                                <RenderTextField label="Previous Survey" name="previousReSurveyReference" readOnly={readOnly} />
                                            </Grid>
                                            <Grid size={3}>
                                                <Button onClick={() => navigate(getSiblingSiteUrl(location.search, site.previousReSurveyReference ?? ''), { replace: true })}>View</Button>
                                            </Grid>
                                        </Stack>
                                    </Grid>
                                }
                                {(site.nextReSurveyReference !== null || !readOnly) &&
                                    <Grid size={12}>
                                        <Stack direction={"row"} spacing={1}>
                                            <Grid size={9}>
                                                <RenderTextField label="Next Survey" name="nextReSurveyReference" readOnly={readOnly} />
                                            </Grid>
                                            <Grid size={3}>
                                                <Button disabled={site.nextReSurveyReference === null} onClick={() => navigate(getSiblingSiteUrl(location.search, site.nextReSurveyReference ?? ''), { replace: true })}>View</Button>
                                            </Grid>
                                        </Stack>
                                    </Grid>
                                }
                            </DataContainer>
                        }
                        <RoleCheck requriedRole={["Admin", "SuperAdmin"]}>
                            {sessionState.siteListMain === '16' && sessionState.siteListSub === 'B' &&
                                <DataContainer title="Development unit preview">
                                    <Grid size={12}>
                                        <DescriptionsTable
                                            name="descriptions"
                                            control={siteFormContext.control}
                                            readOnly={readOnly}
                                        />
                                    </Grid>
                                </DataContainer>

                            }
                        </RoleCheck>
                    </Grid>
                    <DftConnectivityToolScores
                        score={site.dftConnectivityToolScore}
                        eligible={site.isDftConnectivityToolEligible}
                        enabled={site.isDftConnectivityToolEnabled}
                        readOnly={readOnly}
                    />
                </Grid>
                <Grid container size={12}>
                    {showRoadNetworkDistances && <Grid size={6}>
                        <TableContainer>
                            <Table size={'small'}>
                                <CustomTableHead title="Road Network Distance to Local Developments" />
                                <TableBody>
                                    <CustomTableRow label="Year of Analysis" name="analysisYear" value={site.analysisYear} readOnly={readOnly} />
                                    <CustomTableRow label="Nearest Primary School" name="primarySchoolDistance" value={site.primarySchoolDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Secondary School" name="secondarySchoolDistance" value={site.secondarySchoolDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Local Shop/Corner Shop" name="cornerShopDistance" value={site.cornerShopDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Main Supermarket" name="supermarketDistance" value={site.supermarketDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Doctors Surgery" name="surgeryDistance" value={site.surgeryDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Hospital with Minor Injuries/A & E" name="hospitalDistance" value={site.hospitalDistance} readOnly={readOnly} endAdornment="kilometres" />
                                    <CustomTableRow label="Nearest Sports/Leisure Centre" name="sportsCentreDistance" value={site.sportsCentreDistance} readOnly={readOnly} endAdornment="kilometres" />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>}
                    <Grid size={6}>
                        <TableContainer>
                            <Table size={'small'}>
                                <CustomTableHead title="Census Data" />
                                <TableBody>
                                    <CustomTableRow label="Year of Census" name="censusYear" value={site.censusYear} readOnly={readOnly} />
                                    <CustomTableRow label={censusLabel} name="censusOutputArea" value={site.censusOutputArea} readOnly={readOnly} />
                                    {!isScotland && !isNorthanIreland &&
                                        <CustomTableRow label={"Number of people employed within " + censusLabel} name="censusEmployed" value={site.censusEmployed} readOnly={readOnly} />
                                    }
                                    <CustomTableRow label={"Number of households within " + censusLabel} name="numberOfHouseholdsInArea" value={site.numberOfHouseholdsInArea} readOnly={readOnly} />
                                    <CustomTableRow label={"Number of people living within " + censusLabel} name="numberOfPeopleInArea" value={site.numberOfPeopleInArea} readOnly={readOnly} />
                                    {!isIreland && !isScotland &&
                                        <>
                                            <CustomTableRow label="Area of Census Output Area (hectares)" name="censusArea" value={site.censusArea} readOnly={readOnly} />
                                            <CustomTableRow label="Population density within Census Output Area (per hectare)" name="censusPopulationDensity" value={site.censusPopulationDensity} readOnly={readOnly} />
                                        </>
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <PDFLink label="Guidance Notes on Census Data Collection" container="GeneralDocs" file="Census Notes" />
                    </Grid>
                    <Grid container size={12}>
                        <DataContainer title="Census References">
                            <Typography>
                                England & Wales: <a href="https://www.nomisweb.co.uk" target="_blank" rel="noopener noreferrer">www.nomisweb.co.uk</a>
                            </Typography>
                            <Typography>
                                Scotland: <a href="https://statistics.gov.scot" target="_blank" rel="noopener noreferrer">statistics.gov.scot</a>
                            </Typography>
                            <Typography>
                                Northern Ireland: <a href="https://www.nisra.gov.uk" target="_blank" rel="noopener noreferrer">www.nisra.gov.uk</a>
                            </Typography>
                            <Typography>
                                Republic of Ireland: <a href="https://www.cso.ie/en/census" target="_blank" rel="noopener noreferrer">www.cso.ie/en/census</a>
                            </Typography>
                        </DataContainer>
                    </Grid>
                </Grid>
            </Grid>
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} />
        </FormContainer >
    );
}

